import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { IDLE, PENDING, VIEW, EDIT } from "const"
import { hasAccess, hasProjectAccess, StateType } from "client/utils"
import {
    PRM_RRS_TEMPLATE,
    StoreType,
    RrsTemplateProps,

    LoadAllRrsCategories,
    LoadAllRrsTemplateLabels,
    LoadAllRrsTemplateLabelCategories,
    LoadAllRrsTemplateLevels,
    LoadAllRrsTemplateRiskRanks,
    LoadRrsTemplateById,

    setRrsCategoryList,
    setRrsCategoryChildParent,
    setRrsTemplateLabelList,
    setRrsTemplateLabelCategoryList,
    setRrsTemplateLevelList,
    setRrsTemplateRiskRankList,
    setRrsTemplateEdit,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    PageForm,
    Button,
} from "client/components"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"
import TemplateLabelList from "../labelList"
import RiskMap from "../riskMap"

const TemplateView = ({
    templateId = null,
}: RrsTemplateProps) => {
    const baseRoute = useSelector((state: StoreType) => state.rrsTemplate.baseRoute)
    const template = useSelector((state: StoreType) => state.rrsTemplate.templateEdit)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        setLoadState(PENDING)
        Promise.all([
            LoadRrsTemplateById(templateId, userData.accountId, project.id).then((res) => {
                dispatch(setRrsTemplateEdit(res)) // !! IMPORTANT !! all other modules use "templateEdit"
            }),
            LoadAllRrsCategories(userData.accountId, project.id).then((res) => {
                dispatch(setRrsCategoryList(res.categoryList))
                dispatch(setRrsCategoryChildParent(res.childParent))
            }),
            LoadAllRrsTemplateLabels(templateId).then((labels) => {
                dispatch(setRrsTemplateLabelList(labels))
                const nestedPromiseArray: Promise<any>[] = []

                labels.forEach((label) => {
                    nestedPromiseArray.push(
                        LoadAllRrsTemplateLabelCategories(label.id).then((nestedCategories) => {
                            dispatch(setRrsTemplateLabelCategoryList(label.id, nestedCategories))
                        }),
                        LoadAllRrsTemplateLevels(label.id).then((levels) => {
                            dispatch(setRrsTemplateLevelList(label.id, levels))
                        }),
                    )
                })

                return Promise.all(nestedPromiseArray)
            }),
            LoadAllRrsTemplateRiskRanks(templateId).then((res) => {
                dispatch(setRrsTemplateRiskRankList(res))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [])

    const viewAccess = !project.id
        ? hasAccess(PRM_RRS_TEMPLATE, VIEW)
        : hasProjectAccess(PRM_RRS_TEMPLATE, VIEW)

    const editAccess = !project.id
        ? hasAccess(PRM_RRS_TEMPLATE, EDIT)
        : hasProjectAccess(PRM_RRS_TEMPLATE, EDIT)

    const editUrl = `${baseRoute}/${EDIT}/${templateId}`

    if (!viewAccess) return <AccessDenied />
    if (!template) return <NotFound />

    if (project.id) {
        return (
            <Container>
                <PageContent>
                    {
                        loadState === PENDING
                        && <Loader />
                    }
                    {
                        editAccess && (
                            <PageForm noPaddingTop noPaddingBottom>
                                <Row>
                                    <Col xs={12}>
                                        <Button onClick={() => dispatch(addModal({ url: editUrl }))}>
                                            {lang.edit}
                                        </Button>
                                    </Col>
                                </Row>
                            </PageForm>
                        )
                    }
                    {
                        template.id
                        && <RiskMap templateId={templateId} editAccess={false} showCollapse={false} />
                    }
                </PageContent>
            </Container>
        )
    }

    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${lang.viewTemplate} # ${template.id}`}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <Form />
                {
                    template.id
                    && <TemplateLabelList editAccess={false} />
                }
                {
                    template.id
                    && <RiskMap templateId={templateId} editAccess={false} />
                }
            </PageContent>
        </Container>
    )
}
export default TemplateView
